import React from 'react';
import round from 'lodash/round';
import CircularProgress, {
  CircularProgressProps,
} from '@mui/material/CircularProgress';
import GeneralChart from '@/components/seo/GeneralChart';
import { JobTitleContext } from '@/context/create-context';
import {
  WORK_EXPERIENCE_ARRAY,
  WORK_EXPERIENCE_MAP,
} from '@/constants/job-title';

const Progress = (props: CircularProgressProps & { value: number }) => {
  const { value } = props;

  return (
    <div className="relative inline-flex">
      <CircularProgress
        className="!text-gray-100"
        variant="determinate"
        value={100}
        size={48}
        thickness={5}
      />
      <CircularProgress
        className="!absolute !left-0 !-scale-x-[1] !rotate-90"
        variant="determinate"
        value={value}
        size={48}
        thickness={5}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-opacity-85 text-xs">{`${value}%`}</span>
      </div>
    </div>
  );
};

const RecruitmentExperience = (props: {
  id: string;
  experienceData: Record<string, any>;
}) => {
  const { id, experienceData } = props;

  const {
    jobTitle: {
      job_open_details: {
        new_job_details: { last_month_by_experience: lastMonthByExperience },
      },
    },
    isJobTitleCity,
  } = React.useContext(JobTitleContext);

  const rowsData = React.useMemo(() => {
    if (!lastMonthByExperience || lastMonthByExperience.length === 0) {
      return [];
    }
    const lastMonthData = lastMonthByExperience[0].value;
    const total = lastMonthData.reduce(
      (sum: number, next: { value: number }) => sum + next.value,
      0,
    );

    return lastMonthData
      .sort(
        (
          first: { name: string; value: number },
          second: { name: string; value: number },
        ) =>
          WORK_EXPERIENCE_ARRAY.indexOf(first.name) -
          WORK_EXPERIENCE_ARRAY.indexOf(second.name),
      )
      .map(({ name, value }) => ({
        name: WORK_EXPERIENCE_MAP[name],
        value,
        percentage: round((value / total) * 100, 1),
      }));
  }, [lastMonthByExperience]);

  return (
    <GeneralChart
      id={id}
      title="不同经验岗位需求情况"
      tip={experienceData['tip']}
    >
      <p className="mb-2">
        {experienceData['总结']?.[isJobTitleCity ? '城市趋势' : '全国趋势'] ||
          ''}
      </p>
      <div className="pr-12 my-6">
        <table className="w-full table-fixed text-sm text-center">
          <thead className="border-b border-web-tertiary">
            <tr>
              <th className="py-6 text-left font-normal">工作年限</th>
              <th className="py-6 px-3 font-normal">月度新增职位数</th>
              <th className="py-6 px-3 font-normal">职位占比数</th>
            </tr>
          </thead>
          <tbody>
            {rowsData.map(({ name, value, percentage }, index) => (
              <tr key={index} className="border-b border-web-tertiary">
                <td className="py-6 text-left">{name}</td>
                <td className="py-6 px-3 font-semibold">{value}</td>
                <td className="py-2.5 px-3 flex items-center justify-center">
                  <Progress value={percentage} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-semibold mb-2">市场解读</p>
      <ul className="list-inside !ml-2 mb-2">
        {(experienceData?.['市场解读'] || '' || []).map(
          (item: string, index: number) => (
            <li key={index}>{item}</li>
          ),
        )}
      </ul>
    </GeneralChart>
  );
};

export default RecruitmentExperience;
